import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { settingsApi } from '../../services/api';
import { setModules, toggleModuleLocal, setTheme, setLang, DEFAULT_MODULES } from '../../store';
import { useT } from '../../i18n';
import toast from 'react-hot-toast';

const SESSION_TIMEOUT_OPTS = [
  { value: 0,        label: '∞ Limitsiz' },
  { value: 900,      label: '15 dəq' },
  { value: 1800,     label: '30 dəq' },
  { value: 3600,     label: '1 saat' },
  { value: 7200,     label: '2 saat' },
  { value: 14400,    label: '4 saat' },
  { value: 28800,    label: '8 saat' },
  { value: 86400,    label: '1 gün' },
  { value: 604800,   label: '7 gün' },
  { value: 2592000,  label: '30 gün' },
];

const SESSION_ROLES = [
  { key: 'admin',        label: 'Admin Paneli İşçisi', icon: '⚙', color: '#3b82f6' },
  { key: 'customer',     label: 'Müştəri (Mobil Tətbiq)', icon: '📱', color: '#10b981' },
  { key: 'restaurant',   label: 'Restoran İşçisi (Portal)', icon: '◉', color: '#f59e0b' },
  { key: 'chef',         label: 'Aşpaz / Mətbəx', icon: '🍳', color: '#ef4444' },
  { key: 'barrier_op',   label: 'Şlaqbaum Operatoru', icon: '⊞', color: '#8b5cf6' },
];

const SESSION_STORAGE_KEY = 'bg_session_timeouts';
function loadSessionTimeouts() {
  try { return JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}'); } catch { return {}; }
}

const MODULE_META = {
  barrier:       { icon: '🚧', label: 'Şlaqbaum Sistemi',   desc: 'Avtomatik şlaqbaum idarəetməsi — Bluetooth, internet açılma', color: '#3b82f6' },
  restaurant:    { icon: '🍽️', label: 'Restoran & QR Menyu', desc: 'QR menyu, masa sifarişi, mətbəx ekranı',                      color: '#10b981' },
  wallet:        { icon: '💳', label: 'Elektron Cüzdan',     desc: 'Balans artırma, ödəniş tarixi, transferlər',                   color: '#06b6d4' },
  taxi:          { icon: '🚕', label: 'Taksi Xidməti',       desc: 'Taxi çağırış xidməti — Gələcək mərhələ',                      color: '#f59e0b' },
  delivery:      { icon: '📦', label: 'Çatdırılma',          desc: 'Çatdırılma sifarişlərinin idarəsi',                           color: '#8b5cf6' },
  announcements: { icon: '📢', label: 'Elanlar',             desc: 'Sakin elanları və bildirişləri',                              color: '#ef4444' },
  services:      { icon: '🖥️', label: 'Sistem Monitorinqi',  desc: 'Mikroservislərin real-time sağlamlıq vəziyyəti',              color: '#475569' },
  barber:        { icon: '✂️', label: 'Bərbər',              desc: 'Salon rezervasiyası — Gələcək mərhələ',                       color: '#8b5cf6' },
  news:          { icon: '📰', label: 'Xəbərlər',            desc: 'Xəbər və elanlar lenti',                                      color: '#475569' },
};

function Toggle({ checked, onChange }) {
  return (
    <button onClick={() => onChange(!checked)}
      style={{ width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', background: checked ? 'var(--primary)' : 'var(--border)', position: 'relative', transition: 'background 0.25s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 3, left: checked ? 25 : 3, width: 20, height: 20, borderRadius: '50%', background: 'white', transition: 'left 0.25s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
    </button>
  );
}

export default function Settings() {
  const t = useT();
  const dispatch = useDispatch();
  const reduxModules = useSelector(s => s.modules.list);
  const lang = useSelector(s => s.i18n?.lang || 'az');
  const themeMode = useSelector(s => s.theme?.mode || 'dark');

  // Initialize from redux (already loaded from localStorage or DEFAULT_MODULES)
  const [modules, setLocalModules] = useState(() =>
    reduxModules.length > 0 ? reduxModules : DEFAULT_MODULES
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await settingsApi.getModules();
      // setModules reducer merges with DEFAULT_MODULES, so passing empty/partial is safe
      const list = Array.isArray(data) ? data : [];
      dispatch(setModules(list)); // Redux merges with defaults and saves to localStorage
    } catch {
      // API not available — dispatch empty array so reducer falls back to defaults
      dispatch(setModules([]));
    }
    finally { setLoading(false); }
  }, [dispatch]);

  useEffect(() => { load(); }, [load]);

  // Redux modules dəyişdikdə lokal state-i sinxronla
  // reduxModules always has all DEFAULT_MODULES keys (merged in reducer)
  useEffect(() => {
    if (!dirty) {
      const list = reduxModules.length > 0 ? reduxModules : DEFAULT_MODULES;
      setLocalModules(list);
    }
  }, [reduxModules, dirty]);

  const toggle = (key) => {
    setLocalModules(prev => prev.map(m => m.key === key ? { ...m, isEnabled: !m.isEnabled } : m));
    dispatch(toggleModuleLocal(key)); // Redux-da da dəyiş — sidebar dərhal cavab verir
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await settingsApi.updateAll(modules.map(m => ({ key: m.key, isEnabled: m.isEnabled })));
      const result = Array.isArray(updated) && updated.length > 0 ? updated : modules;
      dispatch(setModules(result));
      setLocalModules(result);
      setDirty(false);
      toast.success(t('settings_save') + ' ✓');
    } catch { toast.error('Xəta baş verdi'); }
    finally { setSaving(false); }
  };

  const enabledCount = modules.filter(m => m.isEnabled).length;

  const [sessionTimeouts, setSessionTimeouts] = useState(loadSessionTimeouts);
  const setRoleTimeout = (roleKey, value) => {
    const updated = { ...sessionTimeouts, [roleKey]: value };
    setSessionTimeouts(updated);
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(updated));
    toast.success('Saxlandı');
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', marginBottom: 6 }}>{t('settings_title')}</h1>
          <p style={{ color: 'var(--text3)', fontSize: 14 }}>Modullar, görünüş və dil parametrləri</p>
        </div>
        {dirty && (
          <button style={S.saveBtn} onClick={save} disabled={saving}>
            {saving ? '⟳ ' + t('saving') : '✓ ' + t('settings_save')}
          </button>
        )}
      </div>

      {/* ── Görünüş & Dil ── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>{t('settings_theme')} & {t('settings_lang')}</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 16 }}>
          {/* Tema */}
          <div>
            <div style={S.fieldLabel}>{t('settings_theme')}</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              {[['dark', '🌙', t('theme_dark')], ['light', '☀️', t('theme_light')], ['glass', '✦', t('theme_glass')]].map(([mode, icon, label]) => (
                <button key={mode}
                  style={{ flex: 1, padding: '12px 8px', borderRadius: 10, border: `2px solid ${themeMode === mode ? 'var(--primary)' : 'var(--border)'}`, background: themeMode === mode ? 'var(--primary)18' : 'var(--bg2)', cursor: 'pointer', transition: 'all 0.2s' }}
                  onClick={() => dispatch(setTheme(mode))}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: themeMode === mode ? 'var(--primary)' : 'var(--text3)' }}>{label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Dil */}
          <div>
            <div style={S.fieldLabel}>{t('settings_lang')}</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              {[['az', '🇦🇿', 'Azərbaycan'], ['en', '🇬🇧', 'English'], ['ru', '🇷🇺', 'Русский']].map(([l, flag, name]) => (
                <button key={l}
                  style={{ flex: 1, padding: '12px 8px', borderRadius: 10, border: `2px solid ${lang === l ? 'var(--primary)' : 'var(--border)'}`, background: lang === l ? 'var(--primary)18' : 'var(--bg2)', cursor: 'pointer', transition: 'all 0.2s' }}
                  onClick={() => dispatch(setLang(l))}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{flag}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: lang === l ? 'var(--primary)' : 'var(--text3)' }}>{name}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modul sayacları ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24, maxWidth: 420 }}>
        {[
          [modules.length, t('total'), 'var(--text)'],
          [enabledCount, t('active'), 'var(--success)'],
          [modules.length - enabledCount, t('inactive'), 'var(--danger)'],
        ].map(([n, l, c]) => (
          <div key={l} style={{ ...S.summaryCard, borderColor: c === 'var(--text)' ? 'var(--border)' : c + '44' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: c, marginBottom: 4 }}>{n}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>{l}</div>
          </div>
        ))}
      </div>

      {/* ── Sessiya & Logout Vaxtı ── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>Sessiya & Logout Vaxtı</div>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20 }}>
          Hər rol üçün avtomatik çıxış müddətini təyin edin. Limitsiz seçildikdə istifadəçi heç vaxt avtomatik çıxmır.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {SESSION_ROLES.map(role => {
            const current = sessionTimeouts[role.key] ?? 0;
            return (
              <div key={role.key} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, background: role.color + '22', color: role.color }}>
                    {role.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{role.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {current === 0 ? '∞ Limitsiz' : SESSION_TIMEOUT_OPTS.find(o => o.value === current)?.label || current + ' san'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {SESSION_TIMEOUT_OPTS.map(opt => (
                    <button key={opt.value} type="button"
                      onClick={() => setRoleTimeout(role.key, opt.value)}
                      style={{
                        padding: '5px 12px', borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                        border: `2px solid ${current === opt.value ? role.color : 'var(--border)'}`,
                        background: current === opt.value ? role.color : 'var(--card)',
                        color: current === opt.value ? 'white' : 'var(--text3)',
                        transition: 'all 0.15s',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Modul kartlar ── */}
      <div style={S.section}>
        <div style={S.sectionTitle}>{t('settings_modules')}</div>
        <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20 }}>
          Deaktiv edilən modul mobil tətbiqdə, admin panelin yan panelindəki naviqasiyada görünmür.
          Dəyişikliklər dərhal sidebar-da əks olunur — saxlamaq üçün "Saxla" düyməsinə basın.
        </p>

        {loading ? <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)' }}>{t('loading')}</div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
            {modules.map(m => {
              const meta = MODULE_META[m.key] || { icon: '◈', label: m.key, color: '#475569', desc: '' };
              return (
                <div key={m.key} style={{ ...S.moduleCard, borderColor: m.isEnabled ? meta.color + '44' : 'var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0, background: (m.isEnabled ? meta.color : 'var(--text3)') + '22', color: m.isEnabled ? meta.color : 'var(--text3)' }}>
                      {meta.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>{meta.label || m.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 6 }}>{meta.desc}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: m.isEnabled ? 'var(--success)' : 'var(--danger)' }}>
                        {m.isEnabled ? '● ' + t('active') : '○ ' + t('inactive')}
                      </div>
                    </div>
                    <Toggle checked={m.isEnabled} onChange={() => toggle(m.key)} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  saveBtn: { background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 10, padding: '10px 22px', fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' },
  section: { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 20 },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 },
  fieldLabel: { fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' },
  summaryCard: { background: 'var(--card)', border: '1px solid', borderRadius: 12, padding: 20, textAlign: 'center' },
  moduleCard: { background: 'var(--bg2)', border: '1px solid', borderRadius: 12, padding: 18, transition: 'border-color 0.3s' },
};
